// @ts-strict-ignore
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../timeHelpers';
import {
    AIAuthorDistribution,
    AICodeAuthorProvider,
    OrgOverviewStatistic,
    StatisticDescription,
    StatisticSampleBucketDataPoint,
    StatisticScope,
    StatisticTimeSeriesDataPoint,
    StatisticUnit,
} from '../../types';
import { antGrey } from '../styles/globalStyles';
import { useChartColors } from '../styles/themeMode';
import { displayOrgOverviewStatisticLabel, tooltipForScope } from './orgOverviewHelpers';
import { flatMap, isNil } from 'lodash';
import { OrgOverviewStackedBarGraph } from './OrgOverviewRecharts';
import { OrgOverviewCardSkeleton, StatSection } from './OrgOverviewStatCards';
import {
    buildAIAuthorDistributionColorByCategory,
    compareAIAuthorDistributionSegments,
    formatAIAuthorDistributionSegmentLabel,
    sortAIAuthorDistributionCategoryLabels,
} from './aiAuthorDistributionChartHelpers';

interface OrgOverviewAIAuthorDistributionV2Props extends OrgOverviewStatistic {
    isLoading?: boolean;
    scope: StatisticScope;
}

const DISTRIBUTION_TOTAL_PERCENT = 100;

export const OrgOverviewAIAuthorDistributionV2: React.FC<OrgOverviewAIAuthorDistributionV2Props> = ({
    type,
    displayText,
    description,
    dataPoints,
    isLoading,
    scope,
}) => {
    const chartColors = useChartColors();
    const title = displayText || displayOrgOverviewStatisticLabel(type);
    const resolvedDescription =
        description && typeof description === 'object' ?
            (description as StatisticDescription)
        :   { description: description as string, isCustom: false };
    const tooltip = resolvedDescription.description || tooltipForScope(scope, type);
    const isCustomTooltip = resolvedDescription.isCustom;
    const typedDataPoints = dataPoints as Array<StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint>;

    const { data, colorByCategory, categoryOrder } = React.useMemo(() => {
        const providerByCategory: Record<string, AICodeAuthorProvider> = {};
        const barGraphData = flatMap(typedDataPoints, (dataPoint) => {
            const distribution = dataPoint.value as AIAuthorDistribution;
            if (isNil(distribution) || !distribution.segments?.length) {
                return [];
            }

            const timeBucket = getDistributionTimeBucketKey(dataPoint);
            const axisLabel = getDistributionAxisLabel(dataPoint);
            const tooltipTitle = getDistributionTooltipTitle(dataPoint);

            return [...distribution.segments]
                .sort(compareAIAuthorDistributionSegments)
                .map(({ name, provider, percentage }) => {
                    const type = formatAIAuthorDistributionSegmentLabel(provider, name);
                    providerByCategory[type] = provider;

                    return {
                        timeBucket,
                        axisLabel,
                        tooltipTitle,
                        type,
                        value: toDistributionChartPercent(percentage),
                        total: DISTRIBUTION_TOTAL_PERCENT,
                    };
                });
        });

        const orderedCategoryLabels = sortAIAuthorDistributionCategoryLabels(
            Array.from(new Set(barGraphData.map((datum) => datum.type))),
            providerByCategory,
        );

        return {
            data: barGraphData,
            categoryOrder: orderedCategoryLabels,
            colorByCategory: buildAIAuthorDistributionColorByCategory(orderedCategoryLabels, providerByCategory),
        };
    }, [typedDataPoints]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                alignItems: 'stretch',
            }}
        >
            {isLoading ?
                <OrgOverviewCardSkeleton />
            :   <>
                    <div style={{ height: 56, flexShrink: 0 }}>
                        <StatSection title={title} tooltip={tooltip} isCustomTooltip={isCustomTooltip} />
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 200,
                            backgroundColor: 'var(--rw-chart-container-bg)',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {data.length ?
                            <div style={{ flex: 1, minHeight: 200 }}>
                                <OrgOverviewStackedBarGraph
                                    data={data}
                                    type={type}
                                    unit={StatisticUnit.Percent}
                                    chartColors={chartColors}
                                    colorByCategory={colorByCategory}
                                    categoryOrder={categoryOrder}
                                    showLegend={false}
                                    hideYAxis
                                    hideXAxis={false}
                                    appendPadding={[12, 20, 28, 20]}
                                />
                            </div>
                        :   <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                <Text
                                    style={{
                                        color: antGrey.grey6,
                                        width: 162,
                                        marginLeft: 20,
                                    }}
                                >
                                    Waiting for more release data.
                                </Text>
                            </div>
                        }
                    </div>
                </>
            }
        </div>
    );
};

function toDistributionChartPercent(percentage: number) {
    return percentage <= 1 ? percentage * DISTRIBUTION_TOTAL_PERCENT : percentage;
}

function getDistributionTimeBucketKey(dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint) {
    if ('timestamp' in dataPoint) {
        return dataPoint.releaseId ?? dataPoint.timestamp;
    }

    return `${dataPoint.sampleStartedAt}|${dataPoint.sampleEndedAt}`;
}

function getDistributionAxisLabel(dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint) {
    if ('timestamp' in dataPoint) {
        return formatCalendarDateLabel(dataPoint.timestamp);
    }

    return `${formatCalendarDateLabel(dataPoint.sampleStartedAt)} - ${formatCalendarDateLabel(
        dataPoint.sampleEndedAt,
    )}`;
}

function getDistributionTooltipTitle(dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint) {
    if ('timestamp' in dataPoint && dataPoint.releaseId) {
        return dataPoint.releaseId;
    }

    if ('timestamp' in dataPoint) {
        return formatCalendarDateLabel(dataPoint.timestamp);
    }

    return `${formatCalendarDateLabel(dataPoint.sampleStartedAt)} - ${formatCalendarDateLabel(
        dataPoint.sampleEndedAt,
    )}`;
}

function formatCalendarDateLabel(value: string) {
    const format = DatetimeFormat.ShortMonthDateYear;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return dayjs(value).format(format);
    }

    return dayjs.utc(value).local().format(format);
}
